import React from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Card, CardContent, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Icons — swapped from MUI to Iconsax (Rahim, 2026-08-24, and the app-wide
// standard per icon-library-iconsax.md).
import {
  Gallery,
  VideoSquare,
  DocumentText1,
  Message2,
  Call,
  ExportSquare,
  Copy,
} from "iconsax-react";

/**
 * TemplateSendPreview
 * Renders previews of templates (header, body, footer, and buttons).
 * Can display either a single selected template or multiple templates.
 */
const TemplateSendPreview = ({ templates = null, singleTemplate = null }) => {
  const { selectedTemplate } = useSelector((state) => state.template);
  const theme = useTheme();

  // Determine which templates to display
  let templatesToDisplay = [];
  if (templates && templates.length > 0) {
    templatesToDisplay = templates;
  } else if (singleTemplate) {
    templatesToDisplay = [singleTemplate];
  } else if (selectedTemplate) {
    templatesToDisplay = [selectedTemplate];
  }

  if (templatesToDisplay.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          No templates available
        </Typography>
      </Box>
    );
  }

  // Component to render a single template preview
  const renderTemplatePreview = (template) => {
    if (!template || !template.components) return null;

    // Find each component by type
    const headerComp = template.components.find(
      (comp) => comp.type === "HEADER"
    );
    const bodyComp = template.components.find(
      (comp) => comp.type === "BODY"
    );
    const footerComp = template.components.find(
      (comp) => comp.type === "FOOTER"
    );
    const buttonsComp = template.components.find(
      (comp) => comp.type === "BUTTONS"
    );
    const carouselComp = template.components.find(
      (comp) => comp.type?.toLowerCase() === "carousel"
    );

    // Determine header format (TEXT, IMAGE, VIDEO, DOCUMENT, etc.)
    const headerFormat = headerComp?.format;

    // Basic text for header, body, and footer
    const headerText = headerComp?.text;
    const bodyText = bodyComp?.text;
    const footerText = footerComp?.text;

    // Buttons array (each button has a type, e.g. QUICK_REPLY, PHONE, LINK, COPY_CODE, etc.)
    const buttonsData = buttonsComp?.buttons || [];

    // Carousel data
    const carouselCards = carouselComp?.cards || [];

    return (
      <Card
        sx={{
          // Was a fixed 250px on sm+ — cramped against how much room this
          // pane actually has now that the dialog's bigger (Rahim, 2026-08-24:
          // "try making it better").
          width: { xs: "100%", sm: "min(100%, 320px)" },
          maxWidth: { xs: "min(300px, 100%)", sm: 320 },
          mx: { xs: "auto", sm: 0 },
          borderRadius: "10px",
          border: "1px solid",
          borderColor: (theme) => theme.palette.divider,
          backgroundColor: (theme) => theme.palette.background.paper,
          // A flat border alone made it blend into the pane behind it — a
          // little shadow gives it real card presence, same treatment as
          // this session's other new cards (QuickReplyAutocomplete, etc.).
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0px 4px 12px rgba(0,0,0,0.4)"
              : "0px 4px 12px rgba(0,0,0,0.1)",
          padding: 0,
          mb: { xs: 1, sm: 2 },
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ padding: 0 }}>
          {/* Template Name — bottom padding was only 3-4px against 6-8px on
              top, visibly lopsided/cramped (Rahim, 2026-08-24: "add proper
              padding under the heading"). */}
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{
              padding: { xs: "8px", sm: "10px" },
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              fontSize: { xs: "0.6875rem", sm: "12px" }
            }}
          >
            {template.name || 'Template'}
          </Typography>

          {/* Header */}
          {headerComp && headerFormat === "TEXT" && (
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ px: 1.5, pt: 1.25 }}
            >
              {headerText}
            </Typography>
          )}

          {/* Media placeholders — were hardcoded "gray"/"rgb(219,219,219)",
              invisible-on-dark-mode colors (a real bug this card had, not
              just a style choice — see ui-gotchas.md's hardcoded-light-only
              pattern), plus no set height so the box only ever sized to its
              own padding. Now theme-aware, a real placeholder height, and
              Iconsax icons matching the rest of the app. */}
          {headerComp && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 130,
                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
              }}
            >
              {headerFormat === "IMAGE" && <Gallery size={32} color={theme.palette.text.secondary} variant="Linear" />}
              {headerFormat === "VIDEO" && <VideoSquare size={32} color={theme.palette.text.secondary} variant="Linear" />}
              {headerFormat === "DOCUMENT" && <DocumentText1 size={32} color={theme.palette.text.secondary} variant="Linear" />}
            </Box>
          )}

          {/* Body */}
          {bodyComp && (
            <Typography variant="body2" sx={{ px: 1.5, pt: 1, fontSize: { xs: "0.75rem", sm: "0.875rem" }, lineHeight: 1.4 }}>
              {bodyText}
            </Typography>
          )}

          {/* Footer */}
          {footerComp && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: "block", px: 1.5, pt: 0.5, pb: 1.25, fontSize: { xs: "0.6875rem", sm: "0.75rem" } }}
            >
              {footerText}
            </Typography>
          )}

          <Divider />

          {/* Carousel Component — replicating WhatsApp's real carousel-card
              rendering now (Rahim, 2026-08-24: "replicate the whatsapp
              type"): each card is its own mini version of the SAME structure
              the main template uses above — image header, body text, its own
              buttons — not just a name/snippet. Horizontally swipeable, with
              the next card peeking in at the edge as a real affordance hint,
              plus dot indicators underneath. All content pulled from each
              card's real `components` (same shape TemplateSendBox.jsx already
              reads for sending), nothing fabricated beyond the placeholder
              image itself (WhatsApp doesn't send us actual image bytes here). */}
          {carouselComp && carouselCards.length > 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  // Side spacing now lives on the first/last card's own
                  // margin (below), not container px — see that comment.
                  pt: 1.25,
                  pb: 1,
                  overflowX: "auto",
                  // Lets the last card peek in half-cut at the edge — the
                  // classic "there's more, swipe" visual cue real carousels
                  // use, instead of the strip ending flush with the card edge.
                  scrollSnapType: "x mandatory",
                  // Still scrollable (wheel/trackpad/drag/swipe) — just no
                  // visible track/thumb (Rahim, 2026-08-24: "dont use the
                  // horizontal scroll bar").
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {/* A real spacer element, not margin on the first card — once
                    the strip actually overflows (2+ cards), Chromium/WebKit
                    don't reliably include a flex item's own leading MARGIN in
                    the scrollable content width, so it visually collapsed
                    even though the same margin looked fine with only 1 card
                    (nothing to scroll, so the quirk never triggered) — Rahim,
                    2026-08-24, screenshot: "the first carousel still sticking
                    to the left border". A dedicated spacer box is real
                    layout, not margin, so it can't be dropped the same way. */}
                <Box sx={{ flexShrink: 0, width: 16 }} />
                {carouselCards.map((card, cardIndex) => {
                  const cardBody = card.components?.find((c) => c.type?.toUpperCase() === "BODY");
                  const cardButtons = card.components?.find((c) => c.type?.toUpperCase() === "BUTTONS")?.buttons || [];
                  return (
                    <Box
                      key={cardIndex}
                      sx={{
                        flexShrink: 0,
                        scrollSnapAlign: "start",
                        width: 150,
                        borderRadius: "10px",
                        border: (t) => `1px solid ${t.palette.divider}`,
                        boxShadow: (t) =>
                          t.palette.mode === "dark" ? "0px 2px 8px rgba(0,0,0,0.3)" : "0px 2px 8px rgba(0,0,0,0.08)",
                        overflow: "hidden",
                        bgcolor: "background.paper",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Image header — same tinted-placeholder treatment as
                          the main card's own IMAGE header above. */}
                      <Box
                        sx={{
                          height: 90,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
                          borderBottom: (t) => `1px solid ${t.palette.divider}`,
                        }}
                      >
                        <Gallery size={26} color={theme.palette.text.secondary} variant="Linear" />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.6875rem",
                          lineHeight: 1.35,
                          color: "text.primary",
                          p: 1,
                          pb: cardButtons.length ? 0.5 : 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flex: 1,
                        }}
                      >
                        {cardBody?.text || `Card ${cardIndex + 1}`}
                      </Typography>
                      {/* Each carousel card can carry its own buttons in real
                          WhatsApp templates — same buttonRowSx treatment as
                          the main card's buttons below, scaled down. */}
                      {cardButtons.map((btn, btnIndex) => (
                        <Box
                          key={btnIndex}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                            py: 0.75,
                            px: 0.5,
                            color: "primary.main",
                            fontSize: "0.625rem",
                            fontWeight: 500,
                            borderTop: (t) => `1px solid ${t.palette.divider}`,
                            textAlign: "center",
                          }}
                        >
                          {btn.type?.toUpperCase() === "URL" && <ExportSquare size="12" color={theme.palette.primary.main} variant="Linear" />}
                          {btn.type?.toUpperCase() === "PHONE_NUMBER" && <Call size="12" color={theme.palette.primary.main} variant="Linear" />}
                          {btn.type?.toUpperCase() === "QUICK_REPLY" && <Message2 size="12" color={theme.palette.primary.main} variant="Linear" />}
                          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {btn.text || "Button"}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })}
                {/* Trailing spacer — only when there's a 2nd card to actually
                    peek/scroll toward (see the "useless right spacing" fix
                    on a single card). */}
                {carouselCards.length > 1 && <Box sx={{ flexShrink: 0, width: 16 }} />}
              </Box>
              {/* Dot indicators — a plain swipeable row with no scroll bar
                  visible gives no hint there's more than one card; this is
                  the standard "carousel" affordance. */}
              {carouselCards.length > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, pb: 1.25 }}>
                  {carouselCards.map((_, dotIndex) => (
                    <Box
                      key={dotIndex}
                      sx={{
                        width: dotIndex === 0 ? 12 : 5,
                        height: 5,
                        borderRadius: "3px",
                        bgcolor: dotIndex === 0 ? "primary.main" : "divider",
                        transition: "background-color 0.15s ease",
                      }}
                    />
                  ))}
                </Box>
              )}
              <Divider />
            </>
          )}

          {/* Buttons Section — full-width rows with their own borderTop
              divider now (see buttonRowSx below), so no padding/gap needed
              on this wrapper itself, unlike before. */}
          {buttonsData.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {buttonsData.map((btn, index) => {
                // Real WhatsApp buttons render as full-width tappable rows
                // separated by a divider, not floating centered text — and
                // "#2F8D6B" (a color close to, but not, our actual brand
                // green #0E9046) was hardcoded on every single one instead
                // of using the theme, so it couldn't adapt to dark mode either.
                const buttonRowSx = {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  py: 1,
                  color: "primary.main",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  fontWeight: 500,
                  borderTop: (t) => (index > 0 ? `1px solid ${t.palette.divider}` : "none"),
                };
                const iconSize = { xs: "16", sm: "18" };

                // Each button has a type. Check which type it is and render accordingly:
                switch (btn.type?.toUpperCase()) {
                  case "QUICK_REPLY":
                    return (
                      <Box key={index} sx={buttonRowSx}>
                        <Message2 size={iconSize.sm} color={theme.palette.primary.main} variant="Linear" />
                        {btn.text || "Quick Reply"}
                      </Box>
                    );

                  case "PHONE_NUMBER":
                    return (
                      <Box key={index} sx={buttonRowSx}>
                        <Call size={iconSize.sm} color={theme.palette.primary.main} variant="Linear" />
                        {btn.text || "Call Button"}
                      </Box>
                    );

                  case "URL":
                    return (
                      <Box key={index} sx={buttonRowSx}>
                        <ExportSquare size={iconSize.sm} color={theme.palette.primary.main} variant="Linear" />
                        {btn.text || "Link Button"}
                      </Box>
                    );

                  case "COPY_CODE":
                    return (
                      <Box key={index} sx={buttonRowSx}>
                        <Copy size={iconSize.sm} color={theme.palette.primary.main} variant="Linear" />
                        {btn.text || "Copy Code Button"}
                      </Box>
                    );

                  default:
                    // Fallback for unknown button types
                    return (
                      <Typography key={index} sx={{ textAlign: "center" }}>
                        {btn.text || "Button"}
                      </Typography>
                    );
                }
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // If displaying multiple templates, use a vertical list layout
  if (templatesToDisplay.length > 1) {
    return (
      <Box width={"100%"} sx={{ maxHeight: { xs: "min(42vh, 400px)", sm: "400px" }, overflowY: 'auto', WebkitOverflowScrolling: "touch" }}>
        {templatesToDisplay.map((template, index) => (
          <Box key={template._id || template.id || index} sx={{ mb: 2 }}>
            {renderTemplatePreview(template)}
          </Box>
        ))}
      </Box>
    );
  }

  // If displaying a single template, use the original centered layout
  return (
    <Box width={"100%"} display={"flex"} justifyContent={"center"}>
      {renderTemplatePreview(templatesToDisplay[0])}
    </Box>
  );
};

export default TemplateSendPreview;
